import * as React from 'react';
import { Button, makeStyles, tokens, Tooltip } from '@fluentui/react-components';
import {
  AgreementType,
  PREDEFINED_AGREEMENT_TYPES,
  getPredefinedById,
} from '../../services/agreementTypes';
import { AgreementHistoryEntry } from '../../services/agreementHistory';

interface AgreementPaletteProps {
  history: AgreementHistoryEntry | null;
  onSelect: (type: AgreementType) => void;
}

const useStyles = makeStyles({
  grid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginBottom: '8px',
  },
  chip: {
    minWidth: 'auto',
    paddingLeft: '10px',
    paddingRight: '10px',
    fontSize: tokens.fontSizeBase200,
    borderRadius: '8px',
  },
  chipMemory: {
    border: `2px solid ${tokens.colorBrandStroke1}`,
    color: tokens.colorBrandForeground1,
  },
});

/**
 * Build the bubble list:
 *   - If history is a predefined id → that bubble moves to position 1; rest follow.
 *   - If history is custom → custom is position 1 + first 9 predefined; last predefined drops out.
 *   - If no history → predefined order as-is.
 */
function buildBubbleList(history: AgreementHistoryEntry | null): AgreementType[] {
  if (!history) return PREDEFINED_AGREEMENT_TYPES;

  if (history.isCustom && history.customType) {
    return [history.customType, ...PREDEFINED_AGREEMENT_TYPES.slice(0, 9)];
  }

  const matched = getPredefinedById(history.id);
  if (matched) {
    return [matched, ...PREDEFINED_AGREEMENT_TYPES.filter(t => t.id !== matched.id)];
  }

  return PREDEFINED_AGREEMENT_TYPES;
}

const AgreementPalette: React.FC<AgreementPaletteProps> = ({ history, onSelect }) => {
  const styles = useStyles();
  const bubbles = buildBubbleList(history);
  const memoryId = history?.id ?? null;

  return (
    <div className={styles.grid}>
      {bubbles.map(type => {
        const isMemory = type.id === memoryId;
        return (
          <Tooltip key={type.id} content={type.description} relationship="description">
            <Button
              className={`${styles.chip} ${isMemory ? styles.chipMemory : ''}`}
              size="small"
              appearance="secondary"
              onClick={() => onSelect(type)}
            >
              {type.label}
            </Button>
          </Tooltip>
        );
      })}
    </div>
  );
};

export default AgreementPalette;
